import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useWeb3 } from '@/hooks/use-web3';
import { GETPOOLDETAILS, PoolState, STATE, TOTALFUNDRAISINGMULTIPLIEDPRICE } from '@/lib/common/name-space';
import { ResGetPoolDetailsCall } from '@/types';
import { promiseAllSettled } from '@/lib/Promise';

import Box from '@mui/material/Box';

import Typography from '@mui/material/Typography';
import CommonButton from './CommonButton';
import { useTheme } from '@mui/material';
import { StateFilter } from '@/lib/filter/stakingPoolState';
import StakingModal from './modal/StakingModal';
import SymbolSvg from '@/assets/assetfi/Symbol';
import BigNumber from 'bignumber.js';
import { toast } from 'react-toastify';
import { NOT_CONNECT } from '../common/defineName';
import { setTicker } from '@/component/balance/ticker';

export default function PoolExplain({ contractAddr }: { contractAddr: string }) {
  const [detail, setDetail] = useState<ResGetPoolDetailsCall | null>(null);
  const [totalStake, setTotalStake] = useState<string>('0');
  const [address, setAddress] = useState('');
  const [isOpenStakingModal, setIsOpenStakingModal] = useState(false);
  const [isShowStakeBtn, setIsShowStakeBtn] = useState(false);
  const [usdt, setUsdt] = useState('0');

  const { web3authCall } = useWeb3();
  const poolState = useRef('');
  const theme = useTheme();

  const location = useLocation();
  const path = location.pathname;
  const navigate = useNavigate();
  useEffect(() => {
    if (totalStake === NOT_CONNECT) {
      navigate('/pools');
    }

    return () => {};
  }, [totalStake]);

  useEffect(() => {
    (async () => {
      setUsdt(await setTicker({ navigate: navigate }));
    })();

    return () => {};
  }, []);

  // /poolDetail /portfolioDetail

  // TODO 경로를 받아서 portfolio일 경우 리워드 전체 받기로 변경
  useEffect(() => {
    if (path.startsWith('/poolDetail') && poolState.current === PoolState.POOLSTATE_WAITING) {
      setIsShowStakeBtn(true);
    }
    return () => {};
  }, [path, poolState.current]);
  useEffect(() => {
    if (web3authCall?.connected()) {
      (async () => {
        const addr = await web3authCall.getAddress();
        setAddress(addr);
      })();
    }

    return () => {};
  }, [web3authCall]);

  useEffect(() => {
    if (!contractAddr) {
      // 이전화면으로 이동
      return;
    }
    if (detail) {
      return;
    }
    (async () => {
      const arrPromise = [];

      // const re = await web3authCall.getStakingCall().getPoolDetails('0x64673025Aac24e23d90F89F0F866b90bD521922A');
      arrPromise.push(() => web3authCall?.callStaking(GETPOOLDETAILS, contractAddr));
      arrPromise.push(() => web3authCall?.callStaking(TOTALFUNDRAISINGMULTIPLIEDPRICE, contractAddr));
      arrPromise.push(() => web3authCall?.callStaking(STATE, contractAddr));
      try {
        const result = await promiseAllSettled(arrPromise);
        setDetail(result[0]);
        setTotalStake(result[1]);
        poolState.current = result[2];
      } catch (e) {
        console.log(e);
      }
    })();
    return () => {};
  }, [detail, web3authCall, totalStake, contractAddr]);

  const handleClick = () => {
    if (!detail) {
      return;
    }
    // 1차 예외 처리
    if (BigNumber(detail.minFundraisingPrice).lt(totalStake)) {
      toast.error('The maximum fundraising amount of the pool has been exceeded, so staking is not possible.');
      return;
    }

    setIsOpenStakingModal(true);
  };

  if (!detail) {
    return <div></div>;
  }
  return (
    <>
      {/* PC */}
      <Box
        sx={{
          marginTop: '24px',
          border: `0.5px solid ${theme.greyscale.lightgrey1}`,
          borderRadius: '20px',
          padding: '24px',
          display: { xs: 'none', md: 'flex' },
          gap: '24px',
          justifyContent: 'space-between',
        }}
      >
        {/* 좌측 영역 */}
        <Box>
          <Box>
            <Typography variant="body2" color={theme.greyscale.darkgrey2}>
              {detail.description}
            </Typography>
          </Box>

          <Box sx={{ marginTop: '24px' }}>
            <Typography variant="subtitle1" color={theme.greyscale.grey3}>
              Total Staked
            </Typography>
            <Typography component={'div'} variant="h4" color={theme.greyscale.darkgrey2}>
              {totalStake}{' '}
              <Typography display="inline" component={'div'} variant="h6" color={theme.greyscale.darkgrey2}>
                USD
              </Typography>
            </Typography>

            <Typography component={'div'} variant="h5" color={theme.greyscale.darkgrey2}>
              ~ {BigNumber(totalStake).dividedBy(usdt).toFixed(2)}{' '}
              <Typography display="inline" component={'div'} variant="body3" color={theme.greyscale.darkgrey2}>
                SFL
              </Typography>
            </Typography>
          </Box>
        </Box>

        {/* 우측 영역 */}
        <Box>
          <Typography variant="subtitle1" color={theme.greyscale.grey3}>
            Details
          </Typography>

          <Box
            sx={{
              marginTop: '8px',
              background: theme.background.default,
              borderRadius: '10px',
              border: `0.5px solid ${theme.greyscale.grey1}`,
              minWidth: '335px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Target Amount (Min)
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {detail.minFundraisingPrice}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box>

            {/* <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Target Amount (Max)
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {detail.maxFundraisingPrice}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box> */}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                APR
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {detail.annualInterestMultipliedRate}%
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Minimum Deposit
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {detail.minStakePrice}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Pool Status
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.background.blue}>
                  {StateFilter(poolState.current)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {isShowStakeBtn && address && (
            <CommonButton
              sx={{ marginTop: '16px', width: '100%', borderRadius: '10px' }}
              color={address ? '#ffffff' : theme.background.blue}
              bgColor={address ? theme.background.blue : '#ffffff'}
              text={address ? 'Stake' : 'Connect Wallet'}
              handleClick={handleClick}
              border={theme.background.blue}
              variant={address ? 'h5' : 'h6'}
            />
          )}
        </Box>
      </Box>
      {/* ------------------------------------------------------------------------------------------------------ */}
      {/* Mobile */}
      <Box
        sx={{
          marginTop: '24px',
          border: `0.5px solid ${theme.greyscale.lightgrey1}`,
          borderRadius: '20px',
          padding: '24px',
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          gap: '24px',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <SymbolSvg width={24} height={24} />
          <Box>
            <Typography color={theme.greyscale.darkgrey2} variant={'h6'}>
              {detail.name}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Box>
            <Typography variant="subtitle2" color={theme.greyscale.grey3}>
              Pool Description
            </Typography>
            <Typography variant="body2" color={theme.greyscale.darkgrey2}>
              {detail.description}
            </Typography>
          </Box>

          <Box sx={{ marginTop: '24px' }}>
            <Typography variant="subtitle2" color={theme.greyscale.grey3}>
              Total Staked
            </Typography>
            <Typography component={'div'} variant="h4" color={theme.greyscale.darkgrey2}>
              {totalStake}{' '}
              <Typography display="inline" variant="h6" color={theme.greyscale.darkgrey2}>
                USD
              </Typography>
            </Typography>

            <Typography component={'div'} variant="h5" color={theme.greyscale.darkgrey2}>
              ~ {BigNumber(totalStake).dividedBy(usdt).toFixed(2)}{' '}
              <Typography display="inline" variant="subtitle2" color={theme.greyscale.darkgrey2}>
                SFL
              </Typography>
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography variant="subtitle2" color={theme.greyscale.grey3}>
            Details
          </Typography>

          <Box
            sx={{
              marginTop: '8px',
              background: theme.background.default,
              borderRadius: '10px',
              border: `0.5px solid ${theme.greyscale.grey1}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Target Amount (Min)
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {detail.minFundraisingPrice}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                APR
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {detail.annualInterestMultipliedRate}%
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Minimum Deposit
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {detail.minStakePrice}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Pool Status
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.background.blue}>
                  {StateFilter(poolState.current)}
                </Typography>
              </Box>
            </Box>
          </Box>
          {isShowStakeBtn && address && (
            <CommonButton
              sx={{ marginTop: '16px', width: '100%', borderRadius: '10px' }}
              color={address ? '#ffffff' : theme.background.blue}
              bgColor={address ? theme.background.blue : '#ffffff'}
              text={address ? 'Stake' : 'Connect Wallet'}
              handleClick={handleClick}
              border={theme.background.blue}
              variant={address ? 'h5' : 'h6'}
            />
          )}
        </Box>
      </Box>
      {/* Staking Modal */}
      <StakingModal
        open={isOpenStakingModal}
        handleClose={() => setIsOpenStakingModal(false)}
        contractAddr={contractAddr}
        detail={detail}
        totalAmt={totalStake}
      />
    </>
  );
}
